import { TFunction } from 'i18next'
import { FieldType, IFormConfig } from 'types'
import { PhoneNumberField } from 'components/PhoneNumberField'
import { WebsiteManagementField } from 'components/WebsitesField'
import { BusinessStructure } from 'components/BusinessStructure'
import { AddressFields } from 'components/AddressFields'
import { TaxpayerIdNumberField } from 'components/TaxpayerIdNumberField'
import { yesNoOptions } from 'consts'
import { mapEnumToOptions } from 'utils/enum-tools'
import { CountryAlpha2, TaxNumberType } from 'generated/graphql'
import {
  GeneralInformationForm,
  generalInformationSchema
} from './general-information-schema'

export const getGeneralInformationConfig = (
  t: TFunction
): IFormConfig<GeneralInformationForm> => ({
  schema: generalInformationSchema,
  fields: [
    {
      name: 'businessDetails.legalBusinessName',
      label: t('generalInformation.fields.legalBusinessName.label'),
      placeholder: t('generalInformation.fields.legalBusinessName.placeholder'),
      tooltip: t('generalInformation.fields.legalBusinessName.tooltip')
    },
    {
      name: 'businessStructureDetails.registeredBusinessAddress.addressLine',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.registeredBusinessAddressLine.label'),
      tooltip: t(
        'generalInformation.fields.registeredBusinessAddressLine.tooltip'
      ),
      CustomComponent: AddressFields,
      customComponentProps: {
        country: CountryAlpha2.Us,
        placeholders: {
          addressLine: t(
            'generalInformation.fields.registeredBusinessAddressLine.placeholder'
          ),
          addressLine2: t(
            'generalInformation.fields.registeredBusinessAddressLine2.placeholder'
          ),
          city: t(
            'generalInformation.fields.registeredBusinessCity.placeholder'
          ),
          state: 'State',
          zip: t('generalInformation.fields.registeredBusinessZip.placeholder')
        }
      }
    },
    {
      name: 'businessStructureDetails.businessPhoneNumber',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.businessPhoneNumber.label'),
      tooltip: t('generalInformation.fields.businessPhoneNumber.tooltip'),
      CustomComponent: PhoneNumberField,
      customComponentProps: { lockedCountry: CountryAlpha2.Us }
    },
    {
      name: 'businessDetails.doingBusinessAs',
      label: t('generalInformation.fields.doingBusinessAs.label'),
      placeholder: t('generalInformation.fields.doingBusinessAs.placeholder'),
      tooltip: t('generalInformation.fields.doingBusinessAs.tooltip')
    },
    {
      name: 'businessStructureDetails.sameAsRegisteredAddress',
      type: FieldType.CHECKBOX,
      label: t('generalInformation.fields.sameAsRegisteredAddress.label'),
      checkboxLabel: t(
        'generalInformation.fields.sameAsRegisteredAddress.checkboxLabel'
      ),
      onChange: (checked, methods) => {
        if (!checked) {
          methods.setValue('businessStructureDetails.physicalBusinessAddress', {
            addressLine: '',
            addressLine2: '',
            city: '',
            state: '',
            zip: '',
            countryAlpha2: CountryAlpha2.Us
          })
        }
      }
    },
    {
      name: 'businessStructureDetails.physicalBusinessAddress.addressLine',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.physicalBusinessAddressLine.label'),
      tooltip: t(
        'generalInformation.fields.physicalBusinessAddressLine.tooltip'
      ),
      CustomComponent: AddressFields,
      customComponentProps: {
        country: CountryAlpha2.Us,
        placeholders: {
          addressLine: t(
            'generalInformation.fields.physicalBusinessAddressLine.placeholder'
          ),
          addressLine2: t(
            'generalInformation.fields.physicalBusinessAddressLine2.placeholder'
          ),
          city: t('generalInformation.fields.physicalBusinessCity.placeholder'),
          state: 'State',
          zip: t('generalInformation.fields.physicalBusinessZip.placeholder')
        }
      },
      ignoreField: methods =>
        !!methods.watch('businessStructureDetails.sameAsRegisteredAddress')
    },
    {
      name: 'businessStructureDetails.dbaPhoneNumber',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.dbaPhoneNumber.label'),
      tooltip: t('generalInformation.fields.dbaPhoneNumber.tooltip'),
      CustomComponent: PhoneNumberField
    },
    {
      name: 'customerSupport.phone',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.customerSupportPhone.label'),
      tooltip: t('generalInformation.fields.customerSupportPhone.tooltip'),
      CustomComponent: PhoneNumberField
    },
    {
      name: 'businessStructureDetails.dateOfIncorporation',
      type: FieldType.DATE,
      label: t('generalInformation.fields.dateOfIncorporation.label'),
      placeholder: t(
        'generalInformation.fields.dateOfIncorporation.placeholder'
      ),
      tooltip: t('generalInformation.fields.dateOfIncorporation.tooltip')
    },
    {
      name: 'websites',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.websites.label'),
      CustomComponent: WebsiteManagementField
    },
    {
      name: 'businessStructureDetails.businessStructure',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.businessStructure.label'),
      tooltip: t('generalInformation.fields.businessStructure.tooltip'),
      placeholder: t('generalInformation.fields.businessStructure.placeholder'),
      CustomComponent: BusinessStructure
    },
    {
      name: 'businessStructureDetails.publiclyTradedCorpExchange',
      label: t('generalInformation.fields.publiclyTradedCorpExchange.label'),
      tooltip: t(
        'generalInformation.fields.publiclyTradedCorpExchange.tooltip'
      ),
      ignoreField: methods =>
        methods.watch('businessStructureDetails.businessStructure') !==
        'publicly_traded_corp'
    },
    {
      name: 'businessStructureDetails.publiclyTradedCorporationSymbol',
      label: t(
        'generalInformation.fields.publiclyTradedCorporationSymbol.label'
      ),
      tooltip: t(
        'generalInformation.fields.publiclyTradedCorporationSymbol.tooltip'
      ),
      className: 'uppercase',
      ignoreField: methods =>
        methods.watch('businessStructureDetails.businessStructure') !==
        'publicly_traded_corp'
    },
    {
      name: 'taxInfo.taxpayerName',
      label: t('generalInformation.fields.taxpayerName.label'),
      placeholder: t('generalInformation.fields.taxpayerName.placeholder'),
      tooltip: t('generalInformation.fields.taxpayerName.tooltip')
    },
    {
      name: 'taxInfo.taxpayerIdType',
      type: FieldType.SELECT,
      label: t('generalInformation.fields.taxpayerIdType.label'),
      placeholder: t('generalInformation.fields.taxpayerIdType.placeholder'),
      options: mapEnumToOptions({
        [TaxNumberType.Ein]: t(
          'generalInformation.fields.taxpayerIdType.options.ein'
        ),
        [TaxNumberType.Itin]: t(
          'generalInformation.fields.taxpayerIdType.options.itin'
        ),
        [TaxNumberType.Ssn]: t(
          'generalInformation.fields.taxpayerIdType.options.ssn'
        )
      }),
      onChange: (_value, methods) => {
        methods.setValue('taxInfo.taxpayerIdNumber', '')
      }
    },
    {
      name: 'taxInfo.taxpayerIdNumber',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.taxpayerIdNumber.label'),
      placeholder: t('generalInformation.fields.taxpayerIdNumber.placeholder'),
      tooltip: t('generalInformation.fields.taxpayerIdNumber.tooltip'),
      CustomComponent: TaxpayerIdNumberField
    },
    {
      name: 'taxInfo.sameAsRegisteredAddress',
      type: FieldType.CHECKBOX,
      label: t('generalInformation.fields.taxSameAsRegisteredAddress.label'),
      checkboxLabel: t(
        'generalInformation.fields.taxSameAsRegisteredAddress.checkboxLabel'
      ),
      onChange: (checked, methods) => {
        if (!checked) {
          methods.setValue('taxInfo.address', {
            addressLine: '',
            addressLine2: '',
            city: '',
            state: '',
            zip: '',
            countryAlpha2: CountryAlpha2.Us
          })
        }
      }
    },
    {
      name: 'taxInfo.address.addressLine',
      type: FieldType.CUSTOM,
      label: t('generalInformation.fields.taxAddressLine.label'),
      CustomComponent: AddressFields,
      customComponentProps: {
        country: CountryAlpha2.Us,
        placeholders: {
          addressLine: t(
            'generalInformation.fields.taxAddressLine.placeholder'
          ),
          addressLine2: t(
            'generalInformation.fields.taxAddressLine2.placeholder'
          ),
          city: t('generalInformation.fields.taxCity.placeholder'),
          state: 'State',
          zip: t('generalInformation.fields.taxZip.placeholder')
        }
      },
      ignoreField: methods => !!methods.watch('taxInfo.sameAsRegisteredAddress')
    },
    {
      name: 'taxInfo.exemptPayee',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('generalInformation.fields.exemptPayee.label')
    },
    {
      name: 'taxInfo.taxExempt501c',
      type: FieldType.RADIO,
      options: yesNoOptions,
      label: t('generalInformation.fields.taxExempt501c.label')
    },
    {
      name: 'businessStructureDetails.businessEmail',
      label: t('generalInformation.fields.businessEmail.label'),
      placeholder: t('generalInformation.fields.businessEmail.placeholder')
    },
    {
      name: 'taxInfo.electronicTaxDocumentConsent',
      type: FieldType.CHECKBOX,
      label: t('generalInformation.fields.electronicTaxDocumentConsent.label'),
      checkboxLabel: t(
        'generalInformation.fields.electronicTaxDocumentConsent.checkboxLabel'
      )
    }
  ]
})
